import { useEffect, useState } from "react";

import {
  Print as PrintIcon,
  ReceiptLong as ReceiptLongIcon,
  ViewColumn as ViewColumnIcon,
} from "@mui/icons-material";
import { Alert, Button, Container, LinearProgress, Stack } from "@mui/material";

import { useAuth } from "@/features/authentication";
import {
  buildPreviewBody,
  cloneLayout,
  exitReportFullscreen,
  getFiltersSignature,
  getInitialReportFilters,
  getPageSizeMm,
  InventoryValueReportFilters,
  InventoryValueReportModificationDialog,
  InventoryValueReportMovementDialog,
  InventoryValueReportPrintDialog,
  loadLayoutPreference,
  ReportDocumentViewer,
  ReportInfoMenu,
  ReportSummaryBar,
  saveLayoutPreference,
  toLayoutRequest,
  useInventoryValueReport,
  useInventoryValueReportOptions,
  validateReportFilters,
} from "@/features/inventory-value-report";
import { CenteredLoading, useSnackbar } from "@/features/ui";

const PAGE_TITLE = "Nilai Persediaan";

export const InventoryValueReport = () => {
  const { user, outlet, changeOutlet } = useAuth();
  const { showSnackbar } = useSnackbar();
  const userId = user?.id ?? null;
  const outletId = outlet?.id ?? null;

  const {
    options,
    loading: optionsLoading,
    error: optionsError,
    reload: reloadOptions,
  } = useInventoryValueReportOptions(outletId);
  const report = useInventoryValueReport({ outletId, userId });
  const { load: loadReport } = report;
  const applied = report.document;

  const [filters, setFilters] = useState(getInitialReportFilters);
  const [filterErrors, setFilterErrors] = useState({});
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [layout, setLayout] = useState(null);
  const [preferenceNotice, setPreferenceNotice] = useState(null);
  const [openDialog, setOpenDialog] = useState(null);
  const [printDocument, setPrintDocument] = useState(null);

  // Outlet-specific selections and open report views never survive an outlet or user
  // change; the report hook clears the applied document for the same keys.
  useEffect(() => {
    setFilters((current) => ({ ...current, warehouseIds: null }));
    setFilterErrors({});
    setOpenDialog(null);
    setPrintDocument(null);
  }, [outletId, userId]);

  // The signed-in user's browser-local layout, revalidated against server options.
  useEffect(() => {
    if (!options) {
      setLayout(null);
      return;
    }
    const { layout: saved, notice } = loadLayoutPreference({ userId, options });
    setLayout(saved ?? cloneLayout(options.defaultLayout));
    setPreferenceNotice(notice);
  }, [options, userId]);

  const isStale =
    Boolean(applied) &&
    applied.context?.filtersSignature !== getFiltersSignature(filters);
  // The filter panel shrinks to a summary while a report is shown or being loaded.
  const filtersCollapsed = (Boolean(applied) || report.loading) && !filtersExpanded;

  const updateFilters = (patch) =>
    setFilters((current) => ({ ...current, ...patch }));

  const handleSearch = () => {
    const errors = validateReportFilters({ outlet, filters, options });
    setFilterErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFiltersExpanded(true);
      return;
    }
    if (!options || !layout) return;

    setFiltersExpanded(false);
    loadReport({
      outletId: outlet.id,
      body: buildPreviewBody({ filters, layout, options }),
      context: { filtersSignature: getFiltersSignature(filters), filters },
    });
  };

  // Discards unapplied edits so the summary always describes the report below it.
  const handleCancelFilterEdit = () => {
    setFilters((current) => ({
      ...applied.context.filters,
      sortField: current.sortField,
      sortDirection: current.sortDirection,
    }));
    setFilterErrors({});
    setFiltersExpanded(false);
  };

  // Re-queries the applied selection; the old report stays visible if this fails.
  const regenerateApplied = (body) =>
    loadReport({
      outletId: applied.outletId,
      body,
      context: applied.context,
      keepDocumentOnError: true,
    });

  const handleApplyLayout = async (nextLayout, nextSort) => {
    const applyLocally = () => {
      setLayout(nextLayout);
      updateFilters({ sortField: nextSort.field, sortDirection: nextSort.direction });
      setOpenDialog(null);
    };
    if (!applied) {
      applyLocally();
      return { status: "success" };
    }
    const result = await regenerateApplied({
      ...applied.body,
      layout: toLayoutRequest(nextLayout),
      sort: nextSort,
    });
    if (result.status === "success") applyLocally();
    return result;
  };

  const handleSaveLayout = (nextLayout) => {
    const saved = saveLayoutPreference({ userId, layout: nextLayout, options });
    showSnackbar(
      saved
        ? "Tampilan disimpan untuk akun Anda di browser ini."
        : "Tampilan tidak dapat disimpan di browser ini.",
      saved ? "success" : "error"
    );
  };

  // Dialogs render outside the fullscreen viewer, so leave fullscreen before opening one.
  const openViewerDialog = (name) => {
    exitReportFullscreen();
    setOpenDialog(name);
  };

  const handleOpenPrint = () => {
    // Capture the applied generation now; printing never refetches.
    setPrintDocument({ html: applied.html, metadata: applied.metadata });
    openViewerDialog("print");
  };

  const handleRefreshSummary = () => {
    setOpenDialog(null);
    regenerateApplied(applied.body);
  };

  return (
    // A document viewer page: the title sits in the filter summary instead of a page
    // header, so the report gets as much of the screen as possible.
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Stack spacing={1.5}>
        <InventoryValueReportFilters
          title={PAGE_TITLE}
          outlet={outlet}
          onOutletChange={changeOutlet}
          filters={filters}
          onChange={updateFilters}
          options={options}
          errors={filterErrors}
          disabled={report.loading}
          submitDisabled={report.loading || !options || !layout}
          onSubmit={handleSearch}
          collapsed={filtersCollapsed}
          onExpand={() => setFiltersExpanded(true)}
          onCollapse={applied ? handleCancelFilterEdit : undefined}
        />

        {optionsLoading && <LinearProgress />}
        {optionsError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" sx={{ whiteSpace: "nowrap" }} onClick={reloadOptions}>
                Coba lagi
              </Button>
            }
          >
            Pengaturan laporan gagal dimuat. {optionsError.message}
          </Alert>
        )}
        {options && options.warehouses.length === 0 && (
          <Alert severity="warning">
            Outlet ini belum memiliki gudang yang terhubung, sehingga laporan tidak
            memiliki data.
          </Alert>
        )}
        {preferenceNotice && (
          <Alert severity="info" onClose={() => setPreferenceNotice(null)}>
            {preferenceNotice}
          </Alert>
        )}
        {isStale && (
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                size="small"
                sx={{ whiteSpace: "nowrap" }}
                onClick={handleSearch}
                disabled={report.loading}
              >
                Perbarui
              </Button>
            }
          >
            Filter berubah. Laporan di bawah masih memakai filter sebelumnya.
          </Alert>
        )}

        {report.status === "idle" && (
          <Alert severity="info">
            {outletId
              ? "Atur filter, lalu klik Tampilkan untuk membuat laporan."
              : "Pilih outlet untuk menampilkan laporan."}
          </Alert>
        )}

        {report.status === "loading" && <CenteredLoading sx={{ height: "50vh" }} />}

        {report.status === "error" && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" sx={{ whiteSpace: "nowrap" }} onClick={handleSearch}>
                Coba lagi
              </Button>
            }
          >
            {report.error.message}
            {report.error.requestId ? ` (ID permintaan ${report.error.requestId})` : ""}
          </Alert>
        )}

        {applied && (
          <Stack spacing={1}>
            {report.loading && <LinearProgress />}
            {report.error && (
              <Alert severity="error">
                Laporan terbaru gagal dibuat: {report.error.message} Laporan di
                bawah masih hasil sebelumnya (ID {applied.metadata.reportId}).
              </Alert>
            )}
            {applied.metadata.hasInvalid && (
              <Alert severity="error">
                Laporan memuat nilai tidak valid. Pratinjau ini bersifat diagnostik
                dan cetak final diblokir sampai data diperbaiki.
              </Alert>
            )}
            {report.status === "empty" && (
              <Alert severity="info">Tidak ada data untuk periode dan filter ini.</Alert>
            )}
            <ReportDocumentViewer
              key={applied.metadata.reportId}
              html={applied.html}
              title="Laporan Nilai Persediaan"
              pageWidthMm={getPageSizeMm(applied.body.layout, options)?.width}
              info={<ReportSummaryBar metadata={applied.metadata} />}
              actions={
                <>
                  <Button
                    size="small"
                    startIcon={<ViewColumnIcon />}
                    onClick={() => openViewerDialog("modify")}
                    disabled={!options || !layout || report.loading}
                  >
                    Kolom dan tampilan
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ReceiptLongIcon />}
                    onClick={() => openViewerDialog("movements")}
                    disabled={report.loading}
                  >
                    Rincian pergerakan
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={handleOpenPrint}
                    disabled={report.loading}
                    sx={{ ml: 0.5 }}
                  >
                    Cetak
                  </Button>
                  <ReportInfoMenu metadata={applied.metadata} />
                </>
              }
            />
          </Stack>
        )}
      </Stack>

      {openDialog === "modify" && options && layout && (
        <InventoryValueReportModificationDialog
          open
          options={options}
          layout={layout}
          sort={{ field: filters.sortField, direction: filters.sortDirection }}
          applying={report.loading}
          onCancel={() => setOpenDialog(null)}
          onApply={handleApplyLayout}
          onSave={handleSaveLayout}
        />
      )}
      {openDialog === "print" && printDocument && (
        <InventoryValueReportPrintDialog
          open
          reportDocument={printDocument}
          onClose={() => {
            setOpenDialog(null);
            setPrintDocument(null);
          }}
        />
      )}
      {openDialog === "movements" && applied && options && (
        <InventoryValueReportMovementDialog
          open
          onClose={() => setOpenDialog(null)}
          outletId={applied.outletId}
          userId={userId}
          appliedBody={applied.body}
          warehouses={options.warehouses}
          maxSkuLength={options.limits.maxSkuLength}
          onRefreshSummary={handleRefreshSummary}
        />
      )}
    </Container>
  );
};

export default InventoryValueReport;
